import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const navItems = {
  es: [
    { label: "Inicio", href: "/" },
    { label: "Nosotros", href: "/quienes-somos" },
    { label: "El Show", href: "/the-show" },
    { label: "Eventos", href: "/eventos" },
    { label: "FAQ", href: "/faq" },
    { label: "Aliados", href: "/partners" },
    { label: "Colaborar", href: "/for-partners" },
    { label: "Casos", href: "/selected-work" },
    { label: "Prensa", href: "/press" },
    { label: "Blog", href: "/blog" },
    { label: "Contacto", href: "/contacto" },
  ],
  en: [
    { label: "Home", href: "/" },
    { label: "About", href: "/quienes-somos" },
    { label: "Show", href: "/the-show" },
    { label: "Events", href: "/eventos" },
    { label: "FAQ", href: "/faq" },
    { label: "Partners", href: "/partners" },
    { label: "For Partners", href: "/for-partners" },
    { label: "Work", href: "/selected-work" },
    { label: "Press", href: "/press" },
    { label: "Blog", href: "/blog" },
    { label: "Contact", href: "/contacto" },
  ],
};

export default function SiteHeader({ current = "" }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { i18n } = useTranslation();
  const lang = i18n.language === "en" ? "en" : "es";
  const items = navItems[lang];

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const switchLanguage = (lng) => {
    i18n.changeLanguage(lng);
    window.localStorage.setItem("prel-language", lng);
    document.documentElement.lang = lng;
  };

  return (
    <header className="sticky top-0 z-50 border-b border-white/10 bg-[#06070A]/92 backdrop-blur-xl">
      <nav className="relative max-w-[1440px] mx-auto px-4 sm:px-6 xl:px-8 h-[72px] flex items-center justify-between gap-3 xl:gap-6">
        <a href="/" className="flex items-center gap-3 min-w-0 shrink-0">
          <img src="/logo.png" alt="Puerto Rico Esports League" className="h-8 sm:h-9 w-auto" />
          <span className="hidden 2xl:inline font-semibold tracking-[0.06em] text-[15px] leading-tight text-white/95 whitespace-nowrap">
            PUERTO RICO ESPORTS LEAGUE
          </span>
        </a>

        <a href="/" className="xl:hidden absolute left-1/2 -translate-x-1/2 text-sm sm:text-base font-black tracking-[0.22em] text-white/95 uppercase">
          PREL
        </a>

        <div className="hidden xl:flex items-center justify-center flex-1 min-w-0 gap-4 2xl:gap-5 text-[13px] 2xl:text-[14px] text-slate-300">
          {items.map((item) => {
            const active = current && item.href === current;
            return (
              <a
                key={item.label}
                href={item.href}
                className={`transition-colors whitespace-nowrap ${active ? "text-white font-medium" : "hover:text-white"}`}
              >
                {item.label}
              </a>
            );
          })}
        </div>

        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <div className="hidden md:flex items-center text-[11px] uppercase tracking-[0.12em] text-white/65">
            <button
              type="button"
              onClick={() => switchLanguage("es")}
              className={`transition-colors ${lang === "es" ? "text-white" : "hover:text-white/90"}`}
              aria-label="Cambiar a español"
            >
              ES
            </button>
            <span className="mx-1.5 text-white/20">/</span>
            <button
              type="button"
              onClick={() => switchLanguage("en")}
              className={`transition-colors ${lang === "en" ? "text-white" : "hover:text-white/90"}`}
              aria-label="Switch to English"
            >
              EN
            </button>
          </div>

          <a
            href="/registro"
            className="hidden sm:inline-flex items-center justify-center px-4 xl:px-5 h-10 rounded-full bg-gradient-to-r from-cyan-400 to-violet-500 text-black font-semibold text-sm shadow-[0_0_24px_rgba(34,211,238,.18)] hover:brightness-105 transition"
          >
            {lang === "en" ? "Registration" : "Registro"}
          </a>

          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="xl:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-white/12 bg-white/[0.03] text-white"
            aria-label={lang === "en" ? "Open menu" : "Abrir menú"}
            aria-expanded={mobileOpen}
          >
            <span className="text-lg leading-none">{mobileOpen ? "✕" : "☰"}</span>
          </button>
        </div>
      </nav>

      {mobileOpen ? (
        <div className="xl:hidden border-t border-white/10 bg-[#080B12]/98 backdrop-blur-xl">
          <div className="max-w-[1440px] mx-auto px-4 sm:px-6 py-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3 text-xs uppercase tracking-[0.12em] text-white/70">
                <button
                  type="button"
                  onClick={() => switchLanguage("es")}
                  className={`transition-colors ${lang === "es" ? "text-white" : "hover:text-white/90"}`}
                >
                  ES
                </button>
                <span className="text-white/20">/</span>
                <button
                  type="button"
                  onClick={() => switchLanguage("en")}
                  className={`transition-colors ${lang === "en" ? "text-white" : "hover:text-white/90"}`}
                >
                  EN
                </button>
              </div>

              <a
                href="/registro"
                onClick={() => setMobileOpen(false)}
                className="inline-flex sm:hidden items-center justify-center px-4 h-10 rounded-full bg-gradient-to-r from-cyan-400 to-violet-500 text-black font-semibold text-sm"
              >
                {lang === "en" ? "Registration" : "Registro"}
              </a>
            </div>

            <div className="grid grid-cols-1 gap-2">
              {items.map((item) => {
                const active = current && item.href === current;
                return (
                  <a
                    key={item.label}
                    href={item.href}
                    onClick={() => setMobileOpen(false)}
                    className={`px-3 py-3 rounded-2xl text-[15px] transition ${
                      active
                        ? "bg-white/[0.07] text-white border border-white/10"
                        : "text-slate-200 border border-transparent hover:border-white/10 hover:bg-white/[0.03]"
                    }`}
                  >
                    {item.label}
                  </a>
                );
              })}
            </div>

            <a
              href="/registro"
              onClick={() => setMobileOpen(false)}
              className="hidden sm:inline-flex mt-4 w-full items-center justify-center px-4 h-11 rounded-full bg-gradient-to-r from-cyan-400 to-violet-500 text-black font-semibold text-sm"
            >
              {lang === "en" ? "Registration" : "Registro"}
            </a>
          </div>
        </div>
      ) : null}
    </header>
  );
}
